'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useRouter } from 'next/navigation';

const heroSlides = [
  {
    id: 1,
    image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=1920&q=85',
    alt: 'Panoramic view of Himalayan peaks above clouds with Annapurna range at golden sunrise',
    headline: 'Discover Nepal &',
    highlight: 'The Himalayas',
    sub: 'Journey through ancient trails, sacred valleys, and sky-touching peaks',
    tag: '🏔 Himalayan Adventures',
  },
  {
    id: 2,
    image: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?w=1920&q=85',
    alt: 'Phewa Lake in Pokhara with Annapurna range reflection at sunrise',
    headline: 'Lakeside Serenity',
    highlight: 'in Pokhara',
    sub: "Mirror lakes, paragliding skies, and lakeside serenity await you",
    tag: '🌊 Lakeside Escapes',
  },
  {
    id: 3,
    image: 'https://images.unsplash.com/photo-1582650625119-3a31f8fa2699?w=1920&q=85',
    alt: 'Boudhanath Stupa at dusk with prayer flags and pilgrims in Kathmandu',
    headline: 'Ancient Culture &',
    highlight: 'Living Heritage',
    sub: 'Ancient temples, living goddesses, and thousand-year-old traditions',
    tag: '🏛 Cultural Heritage',
  },
  {
    id: 4,
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=85',
    alt: 'Mountain peaks with snow and dramatic clouds at sunset',
    headline: 'Trek Beyond',
    highlight: 'Your Limits',
    sub: 'From Everest Base Camp to Annapurna Circuit — every trail tells a story',
    tag: '🥾 Epic Treks',
  },
  {
    id: 5,
    image: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1920&q=85',
    alt: 'Aerial view of lush green valleys and terraced fields in Nepal',
    headline: 'Adventure',
    highlight: 'Starts Here',
    sub: 'From hidden villages to wild jungles — your Nepal chapter begins now',
    tag: '✈ Nepal Escapes',
  },
];

const ALL_SEARCH_SUGGESTIONS = [
  'Everest Base Camp Trek', 'Annapurna Circuit', 'Pokhara Lakeside', 'Kathmandu Heritage Tour',
  'Mustang Desert', 'Chitwan Safari', 'Rara Lake', 'Langtang Valley',
  'Hotels in Thamel', 'Luxury Resorts Pokhara', 'Budget Hotels Kathmandu',
  'Local Guides Everest', 'Photography Tours Nepal', 'Paragliding Pokhara',
  'Restaurants Kathmandu', 'Cafes Pokhara', 'Street Food Thamel',
  'Temples Pashupatinath', 'Swayambhunath Stupa', 'Lumbini Buddha Birthplace',
  'Flights Kathmandu to Delhi', 'Cheap Flights Nepal', 'Domestic Flights Nepal',
  'Trekking Packages', 'Adventure Sports Nepal', 'White Water Rafting',
  'Hidden Villages Nepal', 'Off-beat Destinations', 'Community Homestays',
];

const TRENDING = [
  'Everest Base Camp', 'Annapurna Circuit', 'Pokhara', 'Mustang', 'Rara Lake', 'Chitwan Safari',
];

const SEARCH_CATEGORIES = [
  { label: 'Destinations' },
  { label: 'Hotels' },
  { label: 'Flights' },
  { label: 'Guides' },
  { label: 'Packages' },
  { label: 'Restaurants' },
  { label: 'Treks' },
  { label: 'Hidden Gems' },
];

const NEPAL_STATS = [
  { value: 77, suffix: '', label: 'Districts', color: 'var(--glacier-light)' },
  { value: 7, suffix: '', label: 'Provinces', color: 'var(--gold-bright)' },
  { value: 1000, suffix: '+', label: 'Destinations', color: '#6EE7B7' },
  { value: 500, suffix: '+', label: 'Verified Guides', color: '#FCA5A5' },
];

function useCountUp(target: number, duration: number = 1600, active: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, active]);
  return count;
}

function StatItem({ stat, active }: { stat: typeof NEPAL_STATS[0]; active: boolean }) {
  const count = useCountUp(stat.value, 1600, active);
  return (
    <div className="text-center">
      <div className="text-xl md:text-2xl font-serif font-semibold" style={{ color: stat.color }}>
        {count}{stat.suffix}
      </div>
      <div className="text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.45)' }}>{stat.label}</div>
    </div>
  );
}

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Destinations');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [parallaxY, setParallaxY] = useState(0);
  const [airplaneX, setAirplaneX] = useState(-10);
  const [cloudOffset, setCloudOffset] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const slideTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const airplaneTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const router = useRouter();

  useEffect(() => {
    try {
      const h = JSON.parse(localStorage.getItem('yn_search_history') || '[]');
      setSearchHistory(h.slice(0, 5));
    } catch {}
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      if (rect.bottom > 0) setParallaxY(window.scrollY * 0.2);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Intersection observer for stats count-up animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  // Airplane animation
  useEffect(() => {
    let x = -10;
    airplaneTimer.current = setInterval(() => {
      x += 0.15;
      if (x > 110) x = -10;
      setAirplaneX(x);
    }, 50);
    return () => { if (airplaneTimer.current) clearInterval(airplaneTimer.current); };
  }, []);

  // Cloud drift
  useEffect(() => {
    let offset = 0;
    const t = setInterval(() => {
      offset += 0.02;
      setCloudOffset(offset);
    }, 50);
    return () => clearInterval(t);
  }, []);

  const goToSlide = useCallback((idx: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(idx);
    setTimeout(() => setIsTransitioning(false), 1000);
  }, [isTransitioning]);

  // Auto-slide every 5 seconds
  const startSlideTimer = useCallback(() => {
    if (slideTimer.current) clearInterval(slideTimer.current);
    slideTimer.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 5000);
  }, []);

  useEffect(() => {
    startSlideTimer();
    return () => { if (slideTimer.current) clearInterval(slideTimer.current); };
  }, [startSlideTimer]);

  useEffect(() => {
    if (!searchQuery.trim()) { setSuggestions([]); return; }
    const q = searchQuery.toLowerCase();
    const filtered = ALL_SEARCH_SUGGESTIONS.filter(s => s.toLowerCase().includes(q));
    setSuggestions(filtered.slice(0, 8));
  }, [searchQuery]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!searchRef.current?.contains(e.target as Node)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (q?: string) => {
    const query = q || searchQuery;
    if (!query.trim()) return;
    try {
      const h = JSON.parse(localStorage.getItem('yn_search_history') || '[]');
      const updated = [query, ...h.filter((x: string) => x !== query)].slice(0, 10);
      localStorage.setItem('yn_search_history', JSON.stringify(updated));
      setSearchHistory(updated.slice(0, 5));
    } catch {}
    setShowSuggestions(false);
    const catParam = activeCategory !== 'Destinations' ? `&category=${activeCategory.toLowerCase()}` : '';
    router.push(`/search?q=${encodeURIComponent(query)}${catParam}`);
  };

  const handleVoiceSearch = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice search requires Chrome browser.');
      return;
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SR();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    setIsListening(true);
    recognition.start();
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      setIsListening(false);
      handleSearch(transcript);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
  };

  const handleGPSSearch = () => {
    if (!navigator.geolocation) { alert('Geolocation not supported.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        router.push(`/search?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}&nearby=true`);
      },
      () => alert('Location access denied.')
    );
  };

  const slide = heroSlides[currentSlide];

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      aria-label="Hero section"
    >
      {/* Background slides */}
      {heroSlides.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0"
          style={{
            opacity: i === currentSlide ? 1 : 0,
            transform: `translateY(${i === currentSlide ? parallaxY : 0}px) scale(1.06)`,
            willChange: 'transform, opacity',
            transition: 'opacity 1.2s ease',
            zIndex: 0,
          }}
        >
          <AppImage src={s.image} alt={s.alt} fill priority={i === 0} className="object-cover" />
        </div>
      ))}

      {/* Premium dark gradient overlay */}
      <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(to top, rgba(11,45,92,0.98) 0%, rgba(11,45,92,0.72) 30%, rgba(11,45,92,0.42) 60%, rgba(11,45,92,0.18) 100%)' }} />
      <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(to right, rgba(11,45,92,0.55) 0%, transparent 60%)' }} />

      {/* Animated clouds */}
      <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
        {[
          { top: '15%', size: 180, speed: 1, opacity: 0.06 },
          { top: '25%', size: 120, speed: 0.7, opacity: 0.04 },
          { top: '10%', size: 220, speed: 1.3, opacity: 0.05 },
          { top: '35%', size: 90, speed: 0.5, opacity: 0.03 },
        ].map((cloud, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              top: cloud.top,
              left: `${((cloudOffset * cloud.speed * 8 + i * 25) % 130) - 20}%`,
              width: cloud.size,
              height: cloud.size * 0.4,
              background: 'radial-gradient(ellipse, rgba(255,255,255,0.9) 0%, transparent 70%)',
              opacity: cloud.opacity,
              filter: 'blur(20px)',
              transition: 'left 0.1s linear',
            }}
          />
        ))}
      </div>

      {/* Animated birds */}
      <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
        {[
          { top: '20%', delay: 0, scale: 1 },
          { top: '22%', delay: 0.5, scale: 0.7 },
          { top: '19%', delay: 1, scale: 0.8 },
        ].map((bird, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              top: bird.top,
              left: `${((cloudOffset * 1.5 + i * 8 + bird.delay * 10) % 120) - 10}%`,
              transform: `scale(${bird.scale})`,
              opacity: 0.35,
            }}
          >
            <svg width="16" height="8" viewBox="0 0 16 8" fill="none">
              <path d="M0 4 Q4 0 8 4 Q12 0 16 4" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            </svg>
          </div>
        ))}
      </div>

      {/* Animated airplane */}
      <div
        className="absolute z-10 pointer-events-none"
        style={{
          top: '18%',
          left: `${airplaneX}%`,
          transform: 'translateY(-50%)',
          transition: 'left 0.05s linear',
        }}
      >
        <div className="relative flex items-center">
          <div
            className="absolute right-full top-1/2 -translate-y-1/2"
            style={{
              width: 80,
              height: 2,
              background: 'linear-gradient(to left, rgba(244,180,0,0.6), transparent)',
              borderRadius: 2,
            }}
          />
          <svg width="36" height="20" viewBox="0 0 36 20" fill="none">
            <path d="M2 10 L28 4 L34 10 L28 16 L2 10Z" fill="rgba(255,255,255,0.9)" />
            <path d="M14 4 L18 0 L22 4" fill="rgba(244,180,0,0.8)" />
            <path d="M14 16 L18 20 L22 16" fill="rgba(244,180,0,0.8)" />
            <path d="M28 10 L36 10" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
          </svg>
        </div>
      </div>

      {/* Prayer flags top */}
      <div className="absolute top-0 left-0 right-0 flex justify-center z-20 overflow-hidden" style={{ height: 4 }}>
        {Array.from({ length: 80 }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 20,
              height: '100%',
              background: ['#1ABCD4', '#F5F0E8', '#E8621A', '#C8960A', '#22A05A'][i % 5],
              opacity: 0.7,
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-20 flex flex-col items-center text-center px-4 w-full max-w-5xl mx-auto pt-20 pb-4">

        {/* Tag badge */}
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-8 opacity-50" style={{ background: 'var(--gold-bright)' }} />
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.3em] px-4 py-1.5 rounded-full"
            style={{
              color: 'var(--gold-bright)',
              background: 'rgba(244,180,0,0.1)',
              border: '1px solid rgba(244,180,0,0.25)',
              backdropFilter: 'blur(8px)',
            }}
          >
            {slide.tag}
          </span>
          <span className="h-px w-8 opacity-50" style={{ background: 'var(--gold-bright)' }} />
        </div>

        {/* Headline */}
        <h1
          key={`h-${currentSlide}`}
          className="font-serif text-4xl md:text-6xl lg:text-7xl mb-4 max-w-4xl leading-tight"
          style={{
            color: 'var(--himal-white)',
            animation: 'slide-up 0.7s cubic-bezier(0.16,1,0.3,1) forwards',
            textShadow: '0 4px 24px rgba(0,0,0,0.4)',
          }}
        >
          {slide.headline}{' '}
          <span style={{ color: 'var(--gold-bright)', fontStyle: 'italic' }}>{slide.highlight}</span>
        </h1>

        <p
          key={`s-${currentSlide}`}
          className="text-base md:text-lg max-w-xl mb-6 leading-relaxed"
          style={{
            color: 'rgba(245,240,232,0.72)',
            animation: 'slide-up 0.7s 0.1s cubic-bezier(0.16,1,0.3,1) both',
          }}
        >
          {slide.sub}
        </p>

        {/* Search category tabs */}
        <div className="flex items-center gap-1.5 mb-3 flex-wrap justify-center">
          {SEARCH_CATEGORIES.map((cat) => (
            <button
              key={cat.label}
              onClick={() => setActiveCategory(cat.label)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200"
              style={{
                background: activeCategory === cat.label ? 'rgba(244,180,0,0.2)' : 'rgba(255,255,255,0.06)',
                border: `1px solid ${activeCategory === cat.label ? 'rgba(244,180,0,0.5)' : 'rgba(255,255,255,0.1)'}`,
                color: activeCategory === cat.label ? 'var(--gold-bright)' : 'rgba(245,240,232,0.65)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Global Search Bar — fully responsive, never crops button or placeholder */}
        <div ref={searchRef} className="w-full max-w-3xl relative">
          <div
            className="rounded-2xl p-2 shadow-2xl"
            style={{
              background: 'rgba(11,45,92,0.75)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(244,180,0,0.2)',
              boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(26,188,212,0.08), inset 0 1px 0 rgba(255,255,255,0.06)',
            }}
          >
            <div className="flex items-center gap-1">
              {/* Search icon */}
              <div className="pl-2 shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--glacier)' }}>
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
                </svg>
              </div>

              {/* Input — min-w-0 ensures it shrinks properly */}
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSearch();
                  if (e.key === 'Escape') setShowSuggestions(false);
                }}
                placeholder="Search destinations, hotels, flights, guides..."
                className="flex-1 min-w-0 bg-transparent text-sm py-2.5 px-1 focus:outline-none placeholder:opacity-40 placeholder:text-xs sm:placeholder:text-sm"
                style={{ color: 'var(--himal-white)' }}
                aria-label="Global search"
              />

              {/* AI badge — hidden on small screens */}
              <span
                className="hidden md:flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold shrink-0"
                style={{ background: 'rgba(26,188,212,0.12)', color: 'var(--glacier-light)', border: '1px solid rgba(26,188,212,0.2)' }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z"/></svg>
                AI
              </span>

              {/* GPS nearby */}
              <button
                onClick={handleGPSSearch}
                className="p-2 rounded-xl transition-all hover:bg-white/10 shrink-0 hidden sm:flex"
                style={{ color: 'rgba(245,240,232,0.5)' }}
                aria-label="Search nearby places"
                title="Nearby search"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/><path d="M12 2v3m0 14v3M2 12h3m14 0h3"/>
                </svg>
              </button>

              {/* Voice search */}
              <button
                onClick={handleVoiceSearch}
                className={`p-2 rounded-xl transition-all shrink-0 ${isListening ? 'animate-pulse' : 'hover:bg-white/10'}`}
                style={{ color: isListening ? '#ef4444' : 'rgba(245,240,232,0.5)', background: isListening ? 'rgba(239,68,68,0.15)' : undefined }}
                aria-label="Voice search"
                title="Voice search"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              </button>

              {/* Search button — always visible, never cropped */}
              <button
                onClick={() => handleSearch()}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold shrink-0 flex items-center gap-1.5 transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                style={{
                  background: 'linear-gradient(135deg, #F4B400, #E0AA18)',
                  color: '#0B2D5C',
                  boxShadow: '0 4px 16px rgba(244,180,0,0.35)',
                  minWidth: 'fit-content',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
                </svg>
                <span className="hidden sm:inline">Search</span>
              </button>
            </div>
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && (searchQuery || searchHistory.length > 0) && (
            <div
              className="absolute top-full left-0 right-0 mt-2 rounded-2xl overflow-hidden z-50"
              style={{
                background: 'rgba(11,45,92,0.97)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(244,180,0,0.15)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                animation: 'fade-scale 0.2s ease forwards',
              }}
            >
              {!searchQuery && searchHistory.length > 0 && (
                <div>
                  <div className="px-4 pt-3 pb-1 flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.4)' }}>Recent Searches</span>
                    <button
                      onClick={() => { localStorage.removeItem('yn_search_history'); setSearchHistory([]); }}
                      className="text-[10px] hover:underline"
                      style={{ color: 'rgba(245,240,232,0.3)' }}
                    >Clear</button>
                  </div>
                  {searchHistory.map((h, i) => (
                    <button key={i} onClick={() => { setSearchQuery(h); handleSearch(h); }}
                      className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm transition-colors hover:bg-white/5"
                      style={{ color: 'rgba(245,240,232,0.75)' }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'rgba(245,240,232,0.3)', flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      {h}
                    </button>
                  ))}
                  <div style={{ borderTop: '1px solid rgba(244,180,0,0.08)' }} />
                </div>
              )}

              {suggestions.length > 0 && (
                <div>
                  <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.4)' }}>Suggestions</div>
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => { setSearchQuery(s); handleSearch(s); }}
                      className="w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm transition-colors hover:bg-white/5"
                      style={{ color: 'rgba(245,240,232,0.8)' }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--glacier)', flexShrink: 0 }}>
                        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
                      </svg>
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {!searchQuery && (
                <div>
                  <div style={{ borderTop: '1px solid rgba(244,180,0,0.08)' }} />
                  <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.4)' }}>🔥 Trending Now</div>
                  <div className="px-4 pb-4 flex flex-wrap gap-2">
                    {TRENDING.map((t, i) => (
                      <button
                        key={i}
                        onClick={() => handleSearch(t)}
                        className="text-xs px-3 py-1.5 rounded-full transition-all hover:scale-105"
                        style={{
                          background: 'rgba(244,180,0,0.1)',
                          border: '1px solid rgba(244,180,0,0.2)',
                          color: 'var(--gold-bright)',
                        }}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ borderTop: '1px solid rgba(244,180,0,0.08)' }}>
                <div className="px-4 py-3 flex items-center gap-4 flex-wrap">
                  {[
                    { icon: '🤖', label: 'AI Search' },
                    { icon: '🎙', label: 'Voice' },
                    { icon: '📍', label: 'Nearby' },
                    { icon: '🌐', label: 'Worldwide' },
                  ].map(f => (
                    <span key={f.label} className="flex items-center gap-1 text-[10px]" style={{ color: 'rgba(245,240,232,0.35)' }}>
                      <span>{f.icon}</span>
                      <span>{f.label}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Nepal Stats row — animated count-up on scroll */}
        <div
          ref={statsRef}
          className="flex items-center gap-5 md:gap-10 mt-8 text-center flex-wrap justify-center"
        >
          {NEPAL_STATS.map((stat, i) => (
            <React.Fragment key={stat.label}>
              {i > 0 && <div className="h-8 w-px hidden sm:block" style={{ background: 'rgba(245,240,232,0.1)' }} />}
              <StatItem stat={stat} active={statsVisible} />
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Slide dot indicators — 5 small circular dots, centered below stats, never overlapping */}
      <div className="relative z-20 flex items-center justify-center gap-3 mt-5 pb-10">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              goToSlide(i);
              startSlideTimer();
            }}
            aria-label={`Go to slide ${i + 1}`}
            style={{
              width: i === currentSlide ? 10 : 8,
              height: i === currentSlide ? 10 : 8,
              borderRadius: '50%',
              background: i === currentSlide ? '#F4C430' : 'rgba(245,240,232,0.4)',
              border: i === currentSlide ? '2px solid rgba(244,196,48,0.6)' : '1.5px solid rgba(245,240,232,0.2)',
              boxShadow: i === currentSlide ? '0 0 10px rgba(244,196,48,0.7), 0 0 20px rgba(244,196,48,0.3)' : 'none',
              transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
              transform: i === currentSlide ? 'scale(1.15)' : 'scale(1)',
              cursor: 'pointer',
              padding: 0,
              flexShrink: 0,
            }}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 z-20 hidden md:flex flex-col items-center gap-2 opacity-35">
        <span className="text-[9px] uppercase tracking-widest" style={{ color: 'rgba(245,240,232,0.6)' }}>Scroll</span>
        <div className="w-px h-10" style={{ background: 'linear-gradient(to bottom, rgba(245,240,232,0.4), transparent)' }} />
      </div>
    </section>
  );
}